import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchProduct from "../AdminProducts/Products/SearchProduct";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney6(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function round6(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
}

function normalizeIva(value) {
  const iva = Number(value || 0);

  if (iva === 999) return 999;
  if ([0, 5, 15].includes(iva)) return iva;

  return 0;
}

function getIvaLabel(value) {
  if (Number(value) === 999) return "No objeto";
  return `${Number(value || 0)}%`;
}

function getProductFinalPrice(product) {
  return Number(product?.precio_final ?? product?.pvp1 ?? product?.price ?? product?.precio ?? 0);
}

function getProductIva(product) {
  return normalizeIva(product?.iva_porcentaje ?? product?.iva ?? product?.iva_percent ?? 0);
}

function getProductCode(product) {
  return product?.code || product?.codigo || product?.codigo_principal || "";
}

function getProductAuxCode(product) {
  return product?.auxiliary_code || product?.codigo_auxiliar || product?.aux_code || "";
}

function getProductName(product) {
  return product?.name || product?.nombre || "";
}

function calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, cantidad) {
  const finalUnitario = Number(precioConIva || 0);
  const qty = Number(cantidad || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const totalFinal = round6(finalUnitario * qty);

  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(finalUnitario),
      precioConIva: round6(finalUnitario),
      subtotal: totalFinal,
      ivaValor: 0,
      total: totalFinal,
    };
  }

  const subtotalExacto = totalFinal / (1 + iva / 100);
  const subtotal = round6(subtotalExacto);
  const ivaValor = round6(totalFinal - subtotal);
  const precioSinIva = qty > 0 ? round6(subtotalExacto / qty) : 0;

  return {
    precioSinIva,
    precioConIva: round6(finalUnitario),
    subtotal,
    ivaValor,
    total: totalFinal,
  };
}

function calculateLineFromBase(precioSinIva, ivaPorcentaje, cantidad) {
  const baseUnitario = Number(precioSinIva || 0);
  const qty = Number(cantidad || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const subtotal = round6(baseUnitario * qty);

  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(baseUnitario),
      precioConIva: round6(baseUnitario),
      subtotal,
      ivaValor: 0,
      total: subtotal,
    };
  }

  const ivaValor = round6(subtotal * (iva / 100));
  const total = round6(subtotal + ivaValor);
  const precioConIva = round6(baseUnitario + baseUnitario * (iva / 100));

  return {
    precioSinIva: round6(baseUnitario),
    precioConIva,
    subtotal,
    ivaValor,
    total,
  };
}

function buildDetailFromProduct(product, cantidad, descripcion) {
  const qty = round6(cantidad || 0);
  const iva = getProductIva(product);
  const precioFinal = getProductFinalPrice(product);
  const calculo = calculateLineFromFinalPrice(precioFinal, iva, qty);
  const name = getProductName(product);

  return {
    uid: `${product.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    producto_id: product.id,
    codigo_principal: getProductCode(product),
    codigo_auxiliar: getProductAuxCode(product),
    nombre: name,
    descripcion: String(descripcion || "").trim(),
    cantidad: qty,
    precio_unitario: calculo.precioSinIva,
    precio_sin_iva: calculo.precioSinIva,
    precio_con_iva: calculo.precioConIva,
    iva_porcentaje: iva,
    iva_valor: calculo.ivaValor,
    subtotal: calculo.subtotal,
    total: calculo.total,
    observacion: "",
  };
}

function fieldSx(dashboardTheme) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: dashboardTheme.text_color,
      bgcolor: dashboardTheme.input_background || dashboardTheme.card_background,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: dashboardTheme.primary_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.text_color,
    },
    "& input": {
      color: dashboardTheme.text_color,
    },
  };
}

export default function DetailProduct({
  detalles = [],
  resumen,
  onAddDetail,
  onUpdateDetail,
  onRemoveDetail,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cantidad, setCantidad] = useState("1");
  const [descripcion, setDescripcion] = useState("");

  const selectedProductInfo = useMemo(() => {
    if (!selectedProduct) return null;

    const precioFinal = getProductFinalPrice(selectedProduct);
    const iva = getProductIva(selectedProduct);
    const calculo = calculateLineFromFinalPrice(precioFinal, iva, 1);

    return {
      codigo: getProductCode(selectedProduct),
      codigoAuxiliar: getProductAuxCode(selectedProduct),
      nombre: getProductName(selectedProduct),
      precioFinal,
      iva,
      precioSinIva: calculo.precioSinIva,
    };
  }, [selectedProduct]);

  const handleAdd = () => {
    if (!selectedProduct?.id) {
      showError("Selecciona un producto desde el buscador.");
      return;
    }

    const qty = Number(cantidad || 0);

    if (qty <= 0) {
      showError("La cantidad debe ser mayor a cero.");
      return;
    }

    onAddDetail?.(buildDetailFromProduct(selectedProduct, qty, descripcion));
    setSelectedProduct(null);
    setCantidad("1");
    setDescripcion("");
  };

  const handleUpdateField = (detalle, field, value) => {
    const patch = {
      [field]: value,
    };

    if (field === "cantidad") {
      const nuevaCantidad = round6(value || 0);
      const calculo = calculateLineFromBase(
        detalle.precio_sin_iva || detalle.precio_unitario,
        detalle.iva_porcentaje,
        nuevaCantidad
      );

      patch.cantidad = nuevaCantidad;
      patch.precio_unitario = calculo.precioSinIva;
      patch.precio_sin_iva = calculo.precioSinIva;
      patch.precio_con_iva = calculo.precioConIva;
      patch.iva_valor = calculo.ivaValor;
      patch.subtotal = calculo.subtotal;
      patch.total = calculo.total;
    }

    if (field === "precio_sin_iva") {
      const nuevoPrecio = round6(value || 0);
      const calculo = calculateLineFromBase(
        nuevoPrecio,
        detalle.iva_porcentaje,
        detalle.cantidad
      );

      patch.precio_sin_iva = nuevoPrecio;
      patch.precio_unitario = nuevoPrecio;
      patch.precio_con_iva = calculo.precioConIva;
      patch.iva_valor = calculo.ivaValor;
      patch.subtotal = calculo.subtotal;
      patch.total = calculo.total;
    }

    if (field === "iva_porcentaje") {
      const nuevoIva = normalizeIva(value);
      const calculo = calculateLineFromBase(
        detalle.precio_sin_iva || detalle.precio_unitario,
        nuevoIva,
        detalle.cantidad
      );

      patch.iva_porcentaje = nuevoIva;
      patch.precio_con_iva = calculo.precioConIva;
      patch.iva_valor = calculo.ivaValor;
      patch.subtotal = calculo.subtotal;
      patch.total = calculo.total;
    }

    if (field === "descripcion") {
      patch.descripcion = value;
    }

    onUpdateDetail?.(detalle.uid, patch);
  };

  const totalsCard = (
    <Box
      sx={{
        width: { xs: "100%", sm: 420 },
        borderRadius: "14px",
        border: `1px solid ${dashboardTheme.border_color}`,
        p: 2,
        bgcolor: dashboardTheme.card_background,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
            Subtotal sin impuestos
          </Typography>
          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
            ${formatMoney(resumen?.subtotalSinIva)}
          </Typography>
        </Stack>

        {Number(resumen?.baseIva0 || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              Subtotal IVA 0%
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.baseIva0)}
            </Typography>
          </Stack>
        )}

        {Number(resumen?.baseNoObjeto || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              Subtotal no objeto IVA
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.baseNoObjeto)}
            </Typography>
          </Stack>
        )}

        {Number(resumen?.baseIva5 || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              Subtotal IVA 5%
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.baseIva5)}
            </Typography>
          </Stack>
        )}

        {Number(resumen?.iva5 || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              IVA 5%
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.iva5)}
            </Typography>
          </Stack>
        )}

        {Number(resumen?.baseIva15 || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              Subtotal IVA 15%
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.baseIva15)}
            </Typography>
          </Stack>
        )}

        {Number(resumen?.iva15 || 0) > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
              IVA 15%
            </Typography>
            <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
              ${formatMoney(resumen?.iva15)}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
            Valor total IVA
          </Typography>
          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
            ${formatMoney(resumen?.ivaTotal)}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: dashboardTheme.border_color }} />

        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 18, fontWeight: 700 }}>
            Valor total
          </Typography>
          <Typography sx={{ color: dashboardTheme.primary_color, fontSize: 18, fontWeight: 700 }}>
            ${formatMoney(resumen?.total)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: "14px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        overflow: "hidden",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.4,
          borderBottom: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.025)" : "rgba(17,24,39,0.02)",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 20, md: 23 },
            fontWeight: 700,
            color: dashboardTheme.primary_color,
            lineHeight: 1.2,
          }}
        >
          Detalle de Productos
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <SearchProduct
          showEcommerceCheck
          onSelectProduct={(product) => {
            setSelectedProduct(product);
            setCantidad("1");
            setDescripcion("");
          }}
        />

        {selectedProductInfo && (
          <Box
            sx={{
              mt: 2,
              borderRadius: "14px",
              border: `1px solid ${dashboardTheme.border_color}`,
              bgcolor: dashboardTheme.card_background,
              p: { xs: 1.5, md: 2 },
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: dashboardTheme.text_color,
                mb: 1.5,
              }}
            >
              Producto seleccionado
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1.2fr 130px 130px 150px 1.4fr auto",
                },
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: dashboardTheme.text_color }}>
                  {selectedProductInfo.nombre || "-"}
                </Typography>

                <Typography sx={{ fontSize: 12.5, color: dashboardTheme.muted_text_color, mt: 0.35 }}>
                  Código: {selectedProductInfo.codigo || "-"} · Aux: {selectedProductInfo.codigoAuxiliar || "-"}
                </Typography>

                <Typography sx={{ fontSize: 12.5, color: dashboardTheme.primary_color, fontWeight: 700, mt: 0.35 }}>
                  PVP: ${formatMoney(selectedProductInfo.precioFinal)} · SRI: ${formatMoney6(selectedProductInfo.precioSinIva)} · IVA {getIvaLabel(selectedProductInfo.iva)}
                </Typography>
              </Box>

              <TextField
                size="small"
                label="Cantidad"
                type="number"
                value={cantidad}
                onChange={(event) => setCantidad(event.target.value)}
                inputProps={{ step: "any" }}
                sx={fieldSx(dashboardTheme)}
              />

              <TextField
                size="small"
                label="Código"
                value={selectedProductInfo.codigo || ""}
                InputProps={{ readOnly: true }}
                sx={fieldSx(dashboardTheme)}
              />

              <TextField
                size="small"
                label="Cod. auxiliar"
                value={selectedProductInfo.codigoAuxiliar || ""}
                InputProps={{ readOnly: true }}
                sx={fieldSx(dashboardTheme)}
              />

              <TextField
                fullWidth
                size="small"
                label="Descripción del producto"
                value={descripcion}
                onChange={(event) => setDescripcion(event.target.value)}
                placeholder=""
                sx={fieldSx(dashboardTheme)}
              />

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleAdd}
                sx={{
                  minHeight: 40,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: dashboardTheme.button_color,
                  color: dashboardTheme.button_text_color,
                  px: 2,
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    bgcolor: dashboardTheme.button_hover_color,
                    boxShadow: "none",
                  },
                }}
              >
                Añadir
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={dashboardTheme.tableStyles.tableArea}>
        <TableContainer sx={dashboardTheme.tableStyles.container}>
          <Table size="small" sx={dashboardTheme.tableStyles.table}>
            <TableHead>
              <TableRow sx={dashboardTheme.tableStyles.headRow}>
                <TableCell sx={{ ...dashboardTheme.tableStyles.headCell, minWidth: 220 }}>
                  Producto
                </TableCell>

                <TableCell sx={dashboardTheme.tableStyles.headCell}>
                  Código
                </TableCell>

                <TableCell sx={{ ...dashboardTheme.tableStyles.headCell, minWidth: 220 }}>
                  Descripción
                </TableCell>

                <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                  Cant.
                </TableCell>

                <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                  Precio Unit.
                </TableCell>

                <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                  IVA
                </TableCell>

                <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                  Subtotal
                </TableCell>

                <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {detalles.map((detalle) => (
                <TableRow key={detalle.uid} hover sx={dashboardTheme.tableStyles.bodyRow}>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                        lineHeight: 1.25,
                      }}
                    >
                      {detalle.nombre || "-"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: 13.5, color: dashboardTheme.text_color }}>
                      {detalle.codigo_principal || "-"}
                    </Typography>

                    {detalle.codigo_auxiliar && (
                      <Typography sx={{ fontSize: 12, color: dashboardTheme.muted_text_color, mt: 0.25 }}>
                        Aux: {detalle.codigo_auxiliar}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={detalle.descripcion || ""}
                      onChange={(event) => handleUpdateField(detalle, "descripcion", event.target.value)}
                      placeholder=""
                      sx={fieldSx(dashboardTheme)}
                    />
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <TextField
                      size="small"
                      type="number"
                      value={detalle.cantidad}
                      onChange={(event) => handleUpdateField(detalle, "cantidad", event.target.value)}
                      inputProps={{ step: "any" }}
                      sx={{
                        width: 90,
                        ...fieldSx(dashboardTheme),
                      }}
                    />
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <TextField
                      size="small"
                      type="number"
                      value={detalle.precio_sin_iva || detalle.precio_unitario || 0}
                      onChange={(event) => handleUpdateField(detalle, "precio_sin_iva", event.target.value)}
                      inputProps={{ step: "any" }}
                      sx={{
                        width: 120,
                        ...fieldSx(dashboardTheme),
                      }}
                    />
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <TextField
                      select
                      size="small"
                      value={normalizeIva(detalle.iva_porcentaje)}
                      onChange={(event) => handleUpdateField(detalle, "iva_porcentaje", event.target.value)}
                      sx={{
                        width: 110,
                        ...fieldSx(dashboardTheme),
                      }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </TextField>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    ${formatMoney6(detalle.subtotal)}
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteRoundedIcon />}
                      onClick={() => onRemoveDetail?.(detalle.uid)}
                      sx={{
                        minHeight: 34,
                        px: 1.4,
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#dc2626",
                        borderColor: "#dc2626",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#dc2626",
                          bgcolor: "rgba(220,38,38,0.08)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Quitar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {detalles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                    Busca productos y añádelos al detalle de la factura
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          borderTop: `1px solid ${dashboardTheme.border_color}`,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {totalsCard}
      </Box>
    </Paper>
  );
}